import React, { ReactNode, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMenu, FiX, FiBell, FiUser, FiSearch } from 'react-icons/fi';
import AdminNavbar from './AdminNavbar';

interface AdminLayoutProps {
  children: ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navExpanded, setNavExpanded] = useState(true);
  const [searchActive, setSearchActive] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, title: 'New user registered', time: '5 minutes ago', read: false },
    { id: 2, title: 'Transaction completed', time: '1 hour ago', read: false },
    { id: 3, title: 'Expert request pending', time: '3 hours ago', read: true },
  ]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleNavbar = () => {
    setNavExpanded(!navExpanded);
  };

  const toggleSearch = () => {
    setSearchActive(!searchActive);
    if (showNotifications) setShowNotifications(false);
    if (userMenuOpen) setUserMenuOpen(false);
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (searchActive) setSearchActive(false);
    if (userMenuOpen) setUserMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
    if (searchActive) setSearchActive(false);
    if (showNotifications) setShowNotifications(false);
  };
  
  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchActive(false);
    setShowNotifications(false);
    setUserMenuOpen(false);
  }, [location.pathname]);
  
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const unreadNotificationsCount = notifications.filter(n => !n.read).length;
  
  if (isAuthPage) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen bg-background flex flex-col relative overflow-hidden"
      >
        {/* Background elements for futuristic effect */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
          <div className="absolute top-[-40%] left-[-20%] w-[70%] h-[70%] rounded-full bg-gradient-to-r from-primary/20 to-purple-500/10 blur-[120px]" />
          <div className="absolute bottom-[-40%] right-[-20%] w-[70%] h-[70%] rounded-full bg-gradient-to-l from-primary/20 to-blue-500/10 blur-[120px]" />
        </div>
        
        <main className="flex-grow relative z-10">
          {children}
        </main>
      </motion.div>
    );
  }
  
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen flex flex-col overflow-hidden bg-background relative"
    >
      {/* Background elements for futuristic effect */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-40%] left-[-20%] w-[70%] h-[70%] rounded-full bg-gradient-to-r from-primary/20 to-purple-500/10 blur-[120px]" />
        <div className="absolute bottom-[-40%] right-[-20%] w-[70%] h-[70%] rounded-full bg-gradient-to-l from-primary/20 to-blue-500/10 blur-[120px]" />
      </div>
      
      {/* Mobile navbar controls */}
      <div className="fixed top-0 left-0 right-0 h-16 z-40 bg-foreground/80 backdrop-blur-md border-b border-border shadow-sm lg:hidden">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center">
            <button
              onClick={toggleMobileMenu}
              className="p-2 mr-3 rounded-full bg-card/50 hover:bg-card border border-border"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
            </button>
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center text-white font-bold mr-2">
                M
              </div>
              <h1 className="text-lg font-semibold">Matrix Admin</h1>
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={toggleSearch}
              className="p-2 rounded-full bg-card/50 hover:bg-card border border-border"
              aria-label="Search"
            >
              <FiSearch size={18} />
            </button>
            
            <button
              onClick={toggleNotifications}
              className="p-2 rounded-full bg-card/50 hover:bg-card border border-border relative"
              aria-label="Notifications"
            >
              <FiBell size={18} />
              {unreadNotificationsCount > 0 && (
                <span className="absolute top-0 right-0 h-4 w-4 bg-primary text-white text-[10px] font-medium rounded-full flex items-center justify-center transform translate-x-1 -translate-y-1">
                  {unreadNotificationsCount}
                </span>
              )}
            </button>
            
            <button
              onClick={toggleUserMenu}
              className="p-1 rounded-full bg-card/50 hover:bg-card border border-border"
              aria-label="User menu"
            >
              <div className="h-6 w-6 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center text-white text-xs font-bold">
                A
              </div>
            </button>
          </div>
        </div>
        
        {/* Mobile search overlay */}
        {searchActive && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-16 left-0 right-0 p-4 bg-foreground/90 backdrop-blur-md border-b border-border z-30"
          >
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                autoFocus
              />
              <FiSearch size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text/50" />
            </div>
          </motion.div>
        )}
        
        {/* Mobile notifications overlay */}
        {showNotifications && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-16 left-0 right-0 max-h-[70vh] overflow-y-auto bg-foreground/90 backdrop-blur-md border-b border-border z-30"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">Notifications</h3>
                <button className="text-sm text-primary">Mark all as read</button>
              </div>
              
              {notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`p-3 mb-2 rounded-lg border ${notification.read ? 'bg-card/40 border-border' : 'bg-card border-primary/20'}`}
                >
                  <div className="flex items-start">
                    <div className={`p-2 rounded-full ${notification.read ? 'bg-primary/10' : 'bg-primary/20'} mr-3`}>
                      <FiBell size={16} className={notification.read ? 'text-text/70' : 'text-primary'} />
                    </div>
                    <div>
                      <p className={`text-sm ${notification.read ? 'text-text/80' : 'font-medium'}`}>{notification.title}</p>
                      <p className="text-xs text-text/50 mt-1">{notification.time}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              <button className="w-full py-2 text-center text-sm text-primary mt-2">
                View all notifications
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Mobile user menu overlay */}
        {userMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-16 right-0 w-[200px] bg-foreground/90 backdrop-blur-md border-l border-b border-border z-30"
          >
            <div className="p-4">
              <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-border">
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center text-white font-bold">
                  A
                </div>
                <div>
                  <p className="font-medium">Admin User</p>
                  <p className="text-xs text-text/50">admin@matrix.finance</p>
                </div>
              </div>
              
              <ul className="space-y-2">
                <li>
                  <a href="/admin/settings" className="flex items-center py-2 px-3 rounded-lg hover:bg-card text-sm">
                    Profile Settings
                  </a>
                </li>
                <li>
                  <a href="/login" className="flex items-center py-2 px-3 rounded-lg hover:bg-card text-sm">
                    Logout
                  </a>
                </li>
              </ul>
            </div>
          </motion.div>
        )}
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )}
      
      {/* Mobile sidebar */}
      <motion.div 
        initial={{ x: -280 }}
        animate={{ x: mobileMenuOpen ? 0 : -280 }}
        transition={{ type: 'spring', stiffness: 400, damping: 40 }}
        className="fixed inset-y-0 left-0 z-50 w-[280px] bg-foreground/90 backdrop-blur-md border-r border-border lg:hidden overflow-hidden shadow-xl"
      >
        <div className="p-4 overflow-y-auto h-full">
          <AdminNavbar mobile={true} />
        </div>
      </motion.div>
      
      {/* Desktop layout */}
      <div className={`flex flex-grow pt-16 lg:pt-0 relative z-10 ${navExpanded ? 'lg:pl-64' : 'lg:pl-20'}`}>
        {/* Desktop sidebar */}
        <aside className="hidden lg:block fixed top-0 left-0 h-full z-30">
          <AdminNavbar expanded={navExpanded} onToggleExpand={toggleNavbar} />
        </aside>
        
        {/* Top navbar for desktop */}
        <div className="hidden lg:block fixed top-0 right-0 h-16 z-20 bg-foreground/80 backdrop-blur-md border-b border-border shadow-sm transition-all duration-300" style={{ left: navExpanded ? '16rem' : '5rem' }}>
          <div className="h-full flex items-center justify-between px-6">
            <div className="flex items-center">
              <button
                onClick={toggleSearch}
                className="relative p-2 mr-2 rounded-lg hover:bg-card/70 focus:outline-none group"
              >
                <FiSearch size={18} />
                <span className="absolute inset-0 rounded-lg border border-primary/0 group-hover:border-primary/20 group-focus:border-primary/20 transition-colors"></span>
              </button>
              
              {searchActive && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: '300px' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="relative"
                >
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2 bg-card/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                    autoFocus
                  />
                  <FiSearch size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text/50" />
                </motion.div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="relative">
                <button
                  onClick={toggleNotifications}
                  className="relative p-2 rounded-lg hover:bg-card/70 focus:outline-none group"
                  aria-label="Notifications"
                >
                  <FiBell size={18} />
                  {unreadNotificationsCount > 0 && (
                    <span className="absolute top-1 right-1 h-4 w-4 bg-primary text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                      {unreadNotificationsCount}
                    </span>
                  )}
                  <span className="absolute inset-0 rounded-lg border border-primary/0 group-hover:border-primary/20 group-focus:border-primary/20 transition-colors"></span>
                </button>
                
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full right-0 mt-1 w-[320px] bg-foreground/90 backdrop-blur-md border border-border rounded-xl shadow-lg z-50 overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium">Notifications</h3>
                        <button className="text-sm text-primary">Mark all as read</button>
                      </div>
                      
                      <div className="max-h-[60vh] overflow-y-auto">
                        {notifications.map(notification => (
                          <div
                            key={notification.id}
                            className={`p-3 mb-2 rounded-lg border ${notification.read ? 'bg-card/40 border-border' : 'bg-card border-primary/20'}`}
                          >
                            <div className="flex items-start">
                              <div className={`p-2 rounded-full ${notification.read ? 'bg-primary/10' : 'bg-primary/20'} mr-3`}>
                                <FiBell size={16} className={notification.read ? 'text-text/70' : 'text-primary'} />
                              </div>
                              <div>
                                <p className={`text-sm ${notification.read ? 'text-text/80' : 'font-medium'}`}>{notification.title}</p>
                                <p className="text-xs text-text/50 mt-1">{notification.time}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <button className="w-full py-2 text-center text-sm text-primary mt-2">
                        View all notifications
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
              
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-2 p-1 pr-3 rounded-lg hover:bg-card/70 focus:outline-none group"
                  aria-label="User menu"
                >
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center text-white font-bold">
                    A
                  </div>
                  <span className="font-medium text-sm">Admin</span>
                  <span className="absolute inset-0 rounded-lg border border-primary/0 group-hover:border-primary/20 group-focus:border-primary/20 transition-colors"></span>
                </button>
                
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full right-0 mt-1 w-[240px] bg-foreground/90 backdrop-blur-md border border-border rounded-xl shadow-lg z-50 overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-border">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center text-white font-bold">
                          A
                        </div>
                        <div>
                          <p className="font-medium">Admin User</p>
                          <p className="text-xs text-text/50">admin@matrix.finance</p>
                        </div>
                      </div>
                      
                      <ul className="space-y-1">
                        <li>
                          <a href="/admin/settings" className="flex items-center py-2 px-3 rounded-lg hover:bg-card text-sm">
                            Profile Settings
                          </a>
                        </li>
                        <li>
                          <a href="/login" className="flex items-center py-2 px-3 rounded-lg hover:bg-card text-sm">
                            Logout
                          </a>
                        </li>
                      </ul>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Main content */}
        <main className="flex-grow pt-4 lg:pt-20 px-4 lg:px-6 pb-8 max-w-[1800px] mx-auto w-full">
          {children}
        </main>
      </div>
    </motion.div>
  );
};

export default AdminLayout; 